import { useState, useRef } from "react";
import { importData, downloadTemplate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  CheckCircle2, 
  XCircle,
  Loader2,
  FileText,
  AlertCircle
} from "lucide-react";

export default function DataImport() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error("Please select a CSV file");
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.csv')) {
        toast.error("Please drop a CSV file");
        return;
      }
      setFile(droppedFile);
      setResult(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    try {
      const response = await importData(file);
      setResult(response);
      if (response.imported > 0) {
        toast.success(`Successfully imported ${response.imported} record(s)`);
      }
      if (response.errors && response.errors.length > 0) {
        toast.warning(`${response.errors.length} error(s) occurred during import`);
      }
    } catch (error) {
      console.error("Import failed:", error);
      toast.error("Failed to import data");
      setResult({ imported: 0, errors: [error.message] });
    }
    setUploading(false);
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Template downloaded");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download template");
    }
  };

  const clearFile = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Data Import
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Bulk import monthly statistics from a CSV file
        </p>
      </div>

      {/* Template Download */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <FileSpreadsheet className="w-5 h-5 text-sky-600" />
            CSV Template
          </CardTitle>
          <CardDescription>
            Download the template to see the expected format for data import
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            className="border-sky-200 text-sky-700 hover:bg-sky-50"
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
        </CardContent>
      </Card>

      {/* File Upload */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Upload className="w-5 h-5 text-sky-600" />
            Upload Data
          </CardTitle>
          <CardDescription>
            Select or drag a CSV file to import monthly statistics
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-sky-400 hover:bg-sky-50/50 transition-colors cursor-pointer"
            data-testid="file-drop-zone"
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".csv"
              className="hidden"
              data-testid="file-input"
            />
            
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-10 h-10 text-sky-600" />
                <div className="text-left">
                  <p className="font-medium text-slate-900">{file.name}</p>
                  <p className="text-sm text-slate-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                <p className="text-slate-600 font-medium">
                  Drop your CSV file here
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  or click to browse
                </p>
              </>
            )}
          </div>

          {/* Action Buttons */}
          {file && (
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={clearFile}
                data-testid="clear-file-btn"
              >
                Clear
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Data
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Results */}
      {result && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {result.imported > 0 ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              Import Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-emerald-50 rounded-lg">
                <p className="text-sm text-emerald-600">Records Imported</p>
                <p className="text-2xl font-bold text-emerald-700 font-mono">
                  {result.imported}
                </p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-red-600">Errors</p>
                <p className="text-2xl font-bold text-red-700 font-mono">
                  {result.errors?.length || 0}
                </p>
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  Error Details
                </h4>
                <div className="bg-red-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <ul className="space-y-1 text-sm text-red-700">
                    {result.errors.map((error, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-red-400">•</span>
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="border-slate-200 shadow-sm bg-slate-50">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-slate-700">
            Import Instructions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <span className="text-sky-500 font-bold">1.</span>
              Download the CSV template to see the expected column format
            </li>
            <li className="flex items-start gap-2">
              <span className="text-sky-500 font-bold">2.</span>
              Fill in your monthly data (year, month columns are required)
            </li>
            <li className="flex items-start gap-2">
              <span className="text-sky-500 font-bold">3.</span>
              Each row represents one month of data
            </li>
            <li className="flex items-start gap-2">
              <span className="text-sky-500 font-bold">4.</span>
              Existing data for the same year/month will be updated
            </li>
            <li className="flex items-start gap-2">
              <span className="text-sky-500 font-bold">5.</span>
              Upload the file to import all records at once
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
